import { useSeo } from "@/lib/seo";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Building2, KeyRound, ArrowLeft, AlertTriangle, CheckCircle2, ArrowRight, User } from "lucide-react";

export default function Onboarding() {
    useSeo({ title: "Come vuoi iniziare? | VendiDirect", description: "Scegli il profilo: privato, agenzia o solo consultazione.", noindex: true });
    const [step, setStep] = useState("choose"); // choose | code
    const [code, setCode] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState(null);
    const [plans, setPlans] = useState({});
    const nav = useNavigate();
    const { user, setUser } = useAuth();

    useEffect(() => {
        api.get("/subscription/plans").then((r) => {
            const map = {};
            (r.data.plans || []).forEach((p) => { map[p.id] = p; });
            setPlans(map);
        }).catch(() => { /* noop — fallback ai testi generici */ });
    }, []);

    const agenziaPlan = plans.agenzia;
    const privatoPlan = plans.base;
    const freeMonths = agenziaPlan?.free_months || 0;
    const agenziaPrice = agenziaPlan?.price != null ? (Number(agenziaPlan.price) <= 0 ? "Gratuito" : `${agenziaPlan.price}€/mese`) : "129€/mese";
    const privatoPrice = privatoPlan?.price != null ? (Number(privatoPlan.price) <= 0 ? "Gratuito" : `${privatoPlan.price}€`) : "9,99€";

    const submitCode = async () => {
        if (!code.trim()) { setError("Inserisci il codice"); return; }
        if (!user) {
            nav(`/registrazione?account_type=ufficio&office_code=${encodeURIComponent(code.trim().toUpperCase())}`);
            return;
        }
        setSaving(true); setError("");
        try {
            const r = await api.post("/onboarding/redeem-code", { code: code.trim().toUpperCase() });
            setOk(r.data.office);
            // ricarica user context
            const me = await api.get("/auth/me");
            setUser(me.data.user);
            setTimeout(() => nav("/analytics-pro", { replace: true }), 1500);
        } catch (e) {
            setError(e.response?.data?.detail || "Codice non valido");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="onboarding-page" className="min-h-screen bg-ink-950 pt-24 pb-16 px-6">
            <div className="max-w-4xl mx-auto">
                <div className="mb-10 text-center">
                    <span className="label-eyebrow text-gold">Benvenuto in vendidirect.it</span>
                    <h1 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-4">Come vuoi iniziare?</h1>
                    <p className="text-ink-400 mt-3">Scegli il tuo profilo per accedere al gestionale</p>
                </div>

                {step === "choose" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <button
                            data-testid="choose-private-free"
                            onClick={() => nav(user ? "/dashboard" : "/registrazione?account_type=privato", { replace: true })}
                            className="text-left p-8 border border-white/15 hover:border-gold/60 bg-black/40 transition-colors"
                        >
                            <div className="w-11 h-11 border border-gold/60 flex items-center justify-center mb-5">
                                <User size={18} className="text-gold" />
                            </div>
                            <div className="label-eyebrow text-gold">Opzione A</div>
                            <h2 className="font-serif text-2xl text-ink-50 mt-2 mb-3">Sono un privato</h2>
                            <p className="text-sm text-ink-400 leading-relaxed">Continua <b className="text-gold">gratis</b>, in modalità Free (solo lettura). Cerca casa liberamente; potrai attivare un piano solo quando vorrai pubblicare un annuncio (da <b data-testid="onb-private-price" className="text-gold">{privatoPrice}</b>).</p>
                            <div className="mt-6 text-xs text-gold flex items-center gap-2">Continua gratis <ArrowRight size={12} /></div>
                        </button>

                        <button
                            data-testid="choose-new-agency"
                            onClick={() => nav("/prezzi?account_type=agenzia")}
                            className="text-left p-8 border border-white/15 hover:border-gold/60 bg-black/40 transition-colors"
                        >
                            <div className="w-11 h-11 border border-gold/60 flex items-center justify-center mb-5">
                                <Building2 size={18} className="text-gold" />
                            </div>
                            <div className="label-eyebrow text-gold">Opzione B</div>
                            <h2 className="font-serif text-2xl text-ink-50 mt-2 mb-3">Sono una nuova agenzia</h2>
                            <p className="text-sm text-ink-400 leading-relaxed">
                                Attiva il piano Agenzia
                                {freeMonths > 0 ? <> con <b data-testid="onb-agenzia-free-months" className="text-gold">{freeMonths} mes{freeMonths === 1 ? "e" : "i"} gratuit{freeMonths === 1 ? "o" : "i"}</b>, poi</> : <>a</>}
                                {" "}<b data-testid="onb-agenzia-price" className="text-gold">{agenziaPrice}</b>. Include CRM, distribuzione portali, gestione uffici.
                            </p>
                            <div className="mt-6 text-xs text-gold flex items-center gap-2">Continua <ArrowRight size={12} /></div>
                        </button>

                        <button
                            data-testid="choose-office-code"
                            onClick={() => setStep("code")}
                            className="text-left p-8 border border-white/15 hover:border-gold/60 bg-black/40 transition-colors"
                        >
                            <div className="w-11 h-11 border border-gold/60 flex items-center justify-center mb-5">
                                <KeyRound size={18} className="text-gold" />
                            </div>
                            <div className="label-eyebrow text-gold">Opzione C</div>
                            <h2 className="font-serif text-2xl text-ink-50 mt-2 mb-3">Sono un ufficio aggiuntivo</h2>
                            <p className="text-sm text-ink-400 leading-relaxed">Inserisci il <b className="text-gold">codice</b> ricevuto dall'admin della tua agenzia. Accedi immediatamente al CRM, senza pagamento aggiuntivo.</p>
                            <div className="mt-6 text-xs text-gold flex items-center gap-2">Inserisci codice <ArrowRight size={12} /></div>
                        </button>
                    </div>
                )}

                {step === "code" && !ok && (
                    <div className="max-w-md mx-auto border border-gold/40 bg-black/40 p-8">
                        <button onClick={() => setStep("choose")} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1 mb-6"><ArrowLeft size={12} /> Indietro</button>
                        <span className="label-eyebrow text-gold">Ufficio aggiuntivo</span>
                        <h2 className="font-serif text-2xl text-ink-50 mt-2 mb-4">Inserisci il tuo codice</h2>
                        <p className="text-sm text-ink-400 mb-6">Chiedi il codice all'admin della tua agenzia. Ha 8 caratteri (lettere e numeri).</p>
                        <input
                            data-testid="office-code-input"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                            placeholder="ABC12XY9"
                            className="w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 text-2xl font-mono focus:border-gold outline-none tracking-widest text-center"
                        />
                        {error && <div className="mt-4 p-3 border border-red-500/40 bg-red-900/20 text-red-200 text-xs flex items-center gap-2"><AlertTriangle size={12} /> {error}</div>}
                        <button
                            data-testid="submit-code-btn"
                            onClick={submitCode}
                            disabled={saving || code.length < 4}
                            className="w-full mt-6 cta-gold px-5 py-3 text-sm disabled:opacity-50"
                        >
                            {saving ? "Verifica..." : "Attiva ufficio"}
                        </button>
                    </div>
                )}

                {ok && (
                    <div className="max-w-md mx-auto border border-green-500/40 bg-green-900/10 p-8 text-center">
                        <CheckCircle2 size={36} className="text-green-400 mx-auto mb-4" />
                        <h2 className="font-serif text-2xl text-ink-50">Ufficio attivato!</h2>
                        <div className="text-sm text-ink-400 mt-2">{ok.name} · {ok.city}</div>
                        <div className="text-xs text-ink-500 mt-3">Verrai reindirizzato al CRM...</div>
                    </div>
                )}
            </div>
        </div>
    );
}
